// Нижняя панель — целиком из config.navigation.tab_bar: состав, порядок, подписи, иконки. Своего списка нет.
// Прячется при прокрутке вниз и возвращается при прокрутке вверх (Figma, «Скролл вниз»).
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useLocation } from 'react-router-dom';
import type { TabBarItem } from '../api/types';
import { haptic } from '../telegram/webapp';
import { anchorAttr } from './spotlight/anchors';
import { useTourAnchors } from '../tour/launch';
import { ART_TAB_ICONS, TabIcon } from './Icon';
import styles from './TabBar.module.css';

const THRESHOLD = 8; // px за событие: дрожь пальца панель не дёргает
const TOP_ZONE = 80; // у верха страницы панель всегда видна
/** Макетная геометрия панели без безопасной зоны (TabBar.module.css): .tab min-height и отступ снизу. Обычная — Figma
 *  Tabs_classic 48 + 12 (HQ-1887), мира (:root[data-scene]) — прежние 50 + 8. */
const LAYOUT = { classic: { tab: 48, pad: 12 }, scene: { tab: 50, pad: 8 } } as const;
const TOKEN_HEIGHT = 58; // px, --tabbar-height в tokens.css без --tabbar-grow: разницу с панелью несёт --tabbar-grow
const INPUT_WINDOW = 700; // мс: прокрутка без жеста (автоякорь «Сегодня», scrollTo) панель не прячет

/** Шаг прокрутки (Figma «Скролл вниз» 772:33125): вниз жестом — панель уезжает, вверх — возвращается, у верха — видна.
 *  last — точка отсчёта: дрожь меньше порога её не сдвигает, чтобы медленная прокрутка всё же набрала порог. */
export function scrollStep(state: { hidden: boolean; last: number }, y: number, byUser: boolean): { hidden: boolean; last: number } {
  const delta = y - state.last;
  let hidden = state.hidden;
  if (y < TOP_ZONE) hidden = false;
  else if (byUser && delta > THRESHOLD) hidden = true;
  else if (byUser && delta < -THRESHOLD) hidden = false;
  return { hidden, last: !byUser || Math.abs(delta) > THRESHOLD || y < TOP_ZONE ? y : state.last };
}

export function useHideOnScroll(): boolean {
  const [hidden, setHidden] = useState(false);
  const { pathname } = useLocation();
  // новый экран — панель видна и отсчёт прокрутки заново
  useEffect(() => {
    setHidden(false);
    let step = { hidden: false, last: window.scrollY };
    let lastInput = 0;
    const onInput = () => { lastInput = performance.now(); };
    const onScroll = () => {
      step = scrollStep(step, window.scrollY, performance.now() - lastInput < INPUT_WINDOW);
      setHidden(step.hidden);
    };
    const inputs = ['touchmove', 'wheel', 'keydown'] as const;
    inputs.forEach((e) => window.addEventListener(e, onInput, { passive: true }));
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      inputs.forEach((e) => window.removeEventListener(e, onInput));
      window.removeEventListener('scroll', onScroll);
    };
  }, [pathname]);
  useEffect(() => {
    document.documentElement.toggleAttribute('data-tabbar-hidden', hidden);
  }, [hidden]);
  return hidden;
}

/** Есть ли в панели выступающая круглая вкладка (Fantasy, §288): её круг поднят над панелью, и тост/плашка «нет сети»
 *  внизу должны подняться над ним (HQ-1141). Панель мира рисует свои иконки (renderIcon) — круга там нет. */
export function hasRaisedTab(items: TabBarItem[], customIcons: boolean): boolean {
  return !customIcons && items.some((item) => ART_TAB_ICONS.has(item.icon));
}

/** Метка data-tabbar-raised на <html>: по ней tokens.css даёт --tabbar-raise (вынос круга), на неё поднимаются тосты. */
function useRaisedMark(raised: boolean) {
  useEffect(() => {
    if (!raised) return;
    document.documentElement.setAttribute('data-tabbar-raised', '');
    return () => document.documentElement.removeAttribute('data-tabbar-raised');
  }, [raised]);
}

/** Высота панели над --tabbar-height токенов (подпись в 2 строки при крупном шрифте, HQ-1112 v3; панель Figma 60 при
 *  токене 58, HQ-1887) — в --tabbar-grow на <html>: от неё --tabbar-height, а значит отступ содержимого и высота
 *  тостов/плашки «нет сети» над панелью. */
export function tabBarGrow(tabHeights: number[], variant: keyof typeof LAYOUT = 'classic'): number {
  const { tab, pad } = LAYOUT[variant];
  return Math.max(0, Math.ceil(Math.max(tab, ...tabHeights) + pad - TOKEN_HEIGHT));
}

function useGrowMark(ref: RefObject<HTMLElement | null>, count: number) {
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const root = document.documentElement.style;
    const ro = new ResizeObserver(() => {
      const variant = document.documentElement.hasAttribute('data-scene') ? 'scene' : 'classic';
      const grow = tabBarGrow([...el.children].map((tab) => tab.getBoundingClientRect().height), variant);
      if (grow > 0) root.setProperty('--tabbar-grow', `${grow}px`);
      else root.removeProperty('--tabbar-grow');
    });
    [...el.children].forEach((tab) => ro.observe(tab));
    return () => { ro.disconnect(); root.removeProperty('--tabbar-grow'); };
  }, [ref, count]);
}

/** renderIcon — свой набор иконок (панель игрового мира); по умолчанию — иконки вкладок приложения. */
export function TabBar({ items, active, onSelect, renderIcon }: {
  items: TabBarItem[]; active: string; onSelect: (key: string) => void;
  renderIcon?: (name: string, filled: boolean) => ReactNode;
}) {
  const hidden = useHideOnScroll();
  const anchors = useTourAnchors() && !renderIcon;
  const raised = hasRaisedTab(items, !!renderIcon);
  useRaisedMark(raised);
  const ref = useRef<HTMLElement>(null);
  useGrowMark(ref, items.length);
  return (
    <nav ref={ref} className={`${styles.bar} ${hidden ? styles.hidden : ''}`} data-raised={raised ? '' : undefined}>
      {items.map((item) => {
        const selected = item.key === active;
        return (
          <button key={item.key} className={`${styles.tab} ${selected ? styles.selected : ''} ${!renderIcon && ART_TAB_ICONS.has(item.icon) ? styles.big : ''}`}
            aria-current={selected ? 'page' : undefined}
            // якорь тура по приложению tab.{ключ} (HQ-2338) — только у панели приложения и только на время тура
            {...anchorAttr(anchors ? `tab.${item.key}` : null)}
            onClick={() => { if (!selected) haptic.selection(); onSelect(item.key); }}>
            {renderIcon ? renderIcon(item.icon, selected) : <TabIcon name={item.icon} filled={selected} />}
            <span className={styles.label}>{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
